import React, { useEffect, useRef, useState } from "react";
import { api, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, ImagePlus, Pencil, Check, Upload, Loader2, Clock } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const DAYS = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu", "Minggu"];
const empty = { id: null, title: "", media_id: "", days: [...DAYS], start_time: "06:00", end_time: "22:00", duration: 12, enabled: true };

export default function Poster() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const { data } = await api.get("/posters");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setOpen(true); };
  const openEdit = (p) => { setForm({ ...p, days: [...(p.days || [])] }); setOpen(true); };

  const toggleDay = (d) => {
    setForm((f) => ({ ...f, days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d] }));
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, media_id: data.id }));
      toast.success("Poster terunggah");
    } catch {
      toast.error("Gagal mengunggah poster");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.media_id) return toast.error("Unggah gambar poster dulu");
    if (form.days.length === 0) return toast.error("Pilih minimal satu hari tayang");
    const payload = {
      title: form.title, media_id: form.media_id, days: form.days,
      start_time: form.start_time, end_time: form.end_time,
      duration: Number(form.duration) || 12, enabled: form.enabled,
    };
    if (form.id) await api.put(`/posters/${form.id}`, payload);
    else await api.post("/posters", payload);
    toast.success("Jadwal poster disimpan & tampil di layar");
    setOpen(false);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/posters/${id}`);
    toast.success("Poster dihapus");
    load();
  };

  return (
    <div className="space-y-8" data-testid="poster-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-primary">Terjadwal</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">Poster Pengajian</h1>
          <p className="text-sm text-muted-foreground mt-1">Poster tampil otomatis di TV pada hari & jam yang dipilih.</p>
        </div>
        <Button data-testid="poster-new-button" onClick={openNew} className="h-12 px-6 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold gap-2">
          <Plus className="h-5 w-5" /> Tambah Poster
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl py-20 text-center">
          <ImagePlus className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="mt-4 text-muted-foreground">Belum ada poster terjadwal.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="poster-list">
          {items.map((p) => (
            <div key={p.id} className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="aspect-video bg-secondary flex items-center justify-center overflow-hidden">
                <img src={mediaUrl(p.media_id)} alt={p.title} className="w-full h-full object-contain" />
              </div>
              <div className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display font-semibold leading-tight">{p.title || "Tanpa judul"}</h3>
                  <span className={`shrink-0 text-[10px] uppercase tracking-widest px-2 py-1 rounded ${p.enabled ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                    {p.enabled ? "Aktif" : "Nonaktif"}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> {p.start_time}–{p.end_time}</p>
                <div className="flex flex-wrap gap-1">
                  {DAYS.map((d) => (
                    <span key={d} className={`text-[10px] px-1.5 py-0.5 rounded ${p.days?.includes(d) ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground/50"}`}>{d.slice(0, 3)}</span>
                  ))}
                </div>
                <div className="flex gap-2 pt-1">
                  <Button variant="secondary" size="sm" data-testid={`poster-edit-${p.id}`} onClick={() => openEdit(p)} className="gap-2">
                    <Pencil className="h-4 w-4" /> Ubah
                  </Button>
                  <Button variant="ghost" size="icon" data-testid={`poster-delete-${p.id}`} onClick={() => remove(p.id)} className="text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-card border-border max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.id ? "Ubah Poster Terjadwal" : "Tambah Poster Terjadwal"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div>
              <Label>Gambar Poster</Label>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onUpload} data-testid="poster-file-input" />
              <div className="mt-2 flex items-center gap-4">
                <div className="h-28 w-28 rounded-lg border border-border bg-background flex items-center justify-center overflow-hidden shrink-0">
                  {form.media_id ? <img src={mediaUrl(form.media_id)} className="w-full h-full object-contain" alt="poster" /> : <span className="text-xs text-muted-foreground">Belum ada</span>}
                </div>
                <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="poster-upload-button" className="gap-2">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Unggah Gambar
                </Button>
              </div>
            </div>
            <div>
              <Label>Judul (opsional)</Label>
              <Input data-testid="poster-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-2 bg-background" placeholder="mis. Tabligh Akbar" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Hari Tayang</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {DAYS.map((d) => {
                  const sel = form.days.includes(d);
                  return (
                    <button key={d} type="button" data-testid={`poster-day-${d}`} onClick={() => toggleDay(d)} className={`px-3 py-2 rounded-lg text-sm border flex items-center gap-2 ${sel ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground"}`}>
                      {sel && <Check className="h-3.5 w-3.5" />} {d}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Jam Mulai</Label>
                <Input type="time" data-testid="poster-start-input" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="mt-2 bg-background" />
              </div>
              <div>
                <Label>Jam Selesai</Label>
                <Input type="time" data-testid="poster-end-input" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="mt-2 bg-background" />
              </div>
              <div>
                <Label>Durasi (detik)</Label>
                <Input type="number" min="3" data-testid="poster-duration-input" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="mt-2 bg-background" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Label>Tampilkan</Label>
              <Switch checked={form.enabled} data-testid="poster-enabled-switch" onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
            <Button data-testid="poster-save-button" onClick={save} className="bg-primary hover:bg-primary/90 text-primary-foreground">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
